/**
 * Pricing & Offer Decisioning — pure kernel contracts (plan Increment 1).
 *
 * The kernel is deliberately dependency-free: no Firebase, BigQuery, Stripe,
 * browser, or schema-models imports. Builder-service adapters translate owned
 * warehouse/provider shapes into these contracts at the boundary
 * (`src/modules/builder-service/src/services/pricingOfferEmulator/`).
 *
 * Claim taxonomy (plan §3.5): every quantity the kernel emits is labeled
 * `observed` (measured on real exposures), `scenario` (model-projected,
 * confounded, or prior-based), or `blocked` (cannot be evaluated — the reason
 * is named, never silently imputed).
 */

export type ClaimStrength = 'observed' | 'scenario' | 'blocked';

export type OfferInterval = 'week' | 'month' | 'year';

/**
 * Purchase-mode axis (cadence-program D4, C3-I0): one-time/lifetime menu items
 * are representable candidates. Kernel mirror of the shared cadence contract's
 * `PurchaseMode` (`subscriptionLtv/cadenceContract.ts`) — the kernel stays
 * dependency-free, so the declaration lives here too; the adapter-side parity
 * test pins the two declarations together.
 */
export type PurchaseMode = 'subscription' | 'one_time';

export type OfferContractViolation =
  | 'one_time_offer_with_interval'
  | 'subscription_offer_missing_interval'
  | 'one_time_offer_evidence_mismatch'
  | 'subscription_offer_with_one_time_evidence'
  | 'one_time_value_invalid'
  | 'trial_fee_value_invalid'
  | 'pending_cancel_evidence_invalid';

/** Typed contract rejection — never a silent default (C3-AC-001). */
export class OfferContractError extends Error {
  constructor(
    public readonly reason: OfferContractViolation,
    message: string,
  ) {
    super(`${reason}: ${message}`);
    this.name = 'OfferContractError';
  }
}

/** A sellable offer: product+price+trial identity. Kernel never invents offers. */
export interface OfferSpec {
  offerId: string;
  productId: string;
  priceId?: string;
  /**
   * Absent = 'subscription' (pre-axis offers; keeps frozen bundles hashing
   * identically). The default NEVER extends to the interval fields: a
   * subscription offer without an interval and a one_time offer with one are
   * both typed rejections.
   */
  purchaseMode?: PurchaseMode;
  /** Required for subscription offers; FORBIDDEN on one_time offers. */
  interval?: OfferInterval;
  intervalCount?: number;
  /** Nominal price in minor units (audit identity, not used for value math). */
  nominalMinor?: number;
  currency?: string;
  trialDays: number;
  /** observed = seen on real paywall exposures in-window; scenario = hypothetical. */
  claim: Extract<ClaimStrength, 'observed' | 'scenario'>;
}

/**
 * Purchase-mode decision table (C3-AC-001). Called per offer with its value
 * evidence once evidence presence is established; throws `OfferContractError`.
 */
export function validateOfferPurchaseContract(
  offer: OfferSpec,
  evidence: OfferValueEvidence,
): void {
  const mode: PurchaseMode = offer.purchaseMode ?? 'subscription';
  if (mode === 'one_time') {
    if (offer.interval !== undefined || offer.intervalCount !== undefined) {
      throw new OfferContractError(
        'one_time_offer_with_interval',
        `offer ${offer.offerId} is one_time but carries interval fields — a one-time item has no billing interval`,
      );
    }
    if (evidence.kind !== 'one_time') {
      throw new OfferContractError(
        'one_time_offer_evidence_mismatch',
        `offer ${offer.offerId} is one_time but its value evidence is '${evidence.kind}'`,
      );
    }
    return;
  }
  if (offer.interval === undefined || offer.intervalCount === undefined) {
    throw new OfferContractError(
      'subscription_offer_missing_interval',
      `offer ${offer.offerId} is a subscription but has no interval/intervalCount — cadence is never defaulted`,
    );
  }
  if (evidence.kind === 'one_time') {
    throw new OfferContractError(
      'subscription_offer_with_one_time_evidence',
      `offer ${offer.offerId} is a subscription but carries one_time value evidence`,
    );
  }
}

/** A menu = the offer set one paywall screen shows (decision unit, plan §5.5/§9bis.1). */
export interface MenuSpec {
  menuId: string;
  /** Warehouse identity: first_paywall_screen_id. */
  screenId?: string;
  offerIds: string[];
  defaultOfferId?: string;
  claim: Extract<ClaimStrength, 'observed' | 'scenario'>;
}

/**
 * User-grain first-decision choice evidence for one menu.
 * `users` includes non-converters; choices are exclusive counts per offer.
 * Non-choosers = users - sum(choices).
 */
export interface MenuChoiceEvidence {
  menuId: string;
  users: number;
  choices: Record<string, number>;
}

/**
 * Cancellation-draw calibration (D6, C3-I2 — plan §7.7 step 5). The source is
 * part of the TYPE: pending-cancel intent without a calibration is
 * unrepresentable — there is no silent constant. `calibrated` carries the
 * rescind-calibrated P(effective_cancel | cancel_requested) from the shared
 * cancelHazard lane (version + sample); `declared_prior` is the labeled
 * fallback for runs without a registered calibration — the label is recorded
 * on the run output.
 */
export type CancelCalibrationRef =
  | {
      source: 'calibrated';
      pEffectiveGivenRequested: number;
      calibrationVersion: string;
      sampleSize: number;
    }
  | {
      source: 'declared_prior';
      pEffectiveGivenRequested: number;
      priorLabel: string;
    };

/** Pending-cancel intent attached to a cycle-class census (D6). */
export interface PendingCancelEvidence {
  /** Subs with requested-not-rescinded intent and no terminal churn. */
  pendingIntentSubscriptions: number;
  /** Active subs the pending share is measured against. */
  activeSubscriptions: number;
  calibration: CancelCalibrationRef;
}

/** Monthly-class renewal census (sBG doctrine; plan §4.5/§9bis.1 V3). */
export interface RenewalCensusEvidence {
  kind: 'renewal_census';
  r1Numerator: number;
  r1Denominator: number;
  r2Numerator: number;
  r2Denominator: number;
  firstPaymentArpu: number;
  renewalArpu: number;
  /** D6: calibrated pending-cancel adjustment; absent = no intent modeled. */
  pendingCancel?: PendingCancelEvidence;
}

/**
 * Upfront class: prepaid intervals that meet or EXCEED the value horizon
 * (annual at a 12-month horizon) — no in-horizon renewals exist. Weekly plans
 * belong to `weekly_renewal` (cadence-program D1/C2-I1); valuing a weekly
 * candidate at one week's payment was the structural bias against short
 * intervals this class change retires.
 */
export interface UpfrontValueEvidence {
  kind: 'upfront';
  upfrontValue: number;
}

/**
 * Weekly class (D1, cadence-program C2-I1): cycle-based survival in WEEKLY
 * cycles with the horizon derived from the value horizon (12 months → 52
 * weekly cycles). Census fields carry the 7-day opportunity censoring
 * semantics of the shared cadence census (r1 on first-payment age, r2 on
 * first-renewal age); weekly and monthly cycle hazards are never pooled.
 */
export interface WeeklyRenewalEvidence {
  kind: 'weekly_renewal';
  r1Numerator: number;
  r1Denominator: number;
  r2Numerator: number;
  r2Denominator: number;
  firstPaymentArpu: number;
  renewalArpu: number;
  /** D6: calibrated pending-cancel adjustment; absent = no intent modeled. */
  pendingCancel?: PendingCancelEvidence;
}

/** Trial offer: delayed conversion into an underlying paid offer's value. */
export interface TrialValueEvidence {
  kind: 'trial';
  trialToPaidNumerator: number;
  trialToPaidDenominator: number;
  /** offerId whose value the converted trial realizes (comparable horizon rule). */
  underlyingOfferId: string;
  /**
   * Paid-trial fee collected day-0 from EVERY trial starter, SEPARATE from the
   * converted value — never folded into the underlying ARPU (D3, C0-AC-007:
   * `trialFeeValue` 4.99 separate, no double count). Absent = free trial
   * (identical to the pre-field behavior, so frozen bundles keep hashing).
   */
  trialFeeValue?: number;
}

/**
 * One-time/lifetime class (D4, C3-I0): day-0 net collection per buyer, no
 * survival term — the SAME class semantics as the shared model's `oneTime`
 * input (`sbgModel.ts` D4 blend: `netValuePerBuyer`, zero VALID because fully
 * refunded buyers stay entrants, negatives are a typed rejection).
 */
export interface OneTimeValueEvidence {
  kind: 'one_time';
  /** Collection net of refunds, per buyer. Zero valid; negative rejected. */
  netValuePerBuyer: number;
}

export type OfferValueEvidence =
  | RenewalCensusEvidence
  | UpfrontValueEvidence
  | WeeklyRenewalEvidence
  | TrialValueEvidence
  | OneTimeValueEvidence;

/**
 * Exposure integrity lanes (MVP criterion 4): journeys whose offer exposure is
 * missing stay a NAMED lane and are never imputed as baseline silently.
 */
export interface ExposureLanes {
  paywallReached: number;
  exposureCovered: number;
  exposureMissing: number;
}

export type PolicySpec =
  | { policyId: string; kind: 'current_mix'; menuWeights: Record<string, number> }
  | { policyId: string; kind: 'global_menu'; menuId: string }
  | {
      policyId: string;
      kind: 'cohort';
      /** cohort share (0..1, sums to 1) → menu served to that cohort */
      cohorts: Array<{ cohortId: string; share: number; menuId: string }>;
    };

export interface FrontierInput {
  seed: number;
  draws: number;
  /** Value horizon in months — identical for trial and no-trial offers (criterion 5). */
  horizonMonths: number;
  offers: OfferSpec[];
  menus: MenuSpec[];
  choiceEvidence: MenuChoiceEvidence[];
  valueEvidence: Record<string, OfferValueEvidence>;
  exposure: ExposureLanes;
  /** The observed policy — always the comparison baseline. */
  baseline: PolicySpec;
  candidatePolicies: PolicySpec[];
  /** Basis of value inputs — gross is audit-labeled (plan §7.5 degrade policy). */
  valueBasis: 'gross_audit' | 'contribution';
}

export interface FrontierRow {
  policyId: string;
  kind: PolicySpec['kind'];
  p10: number;
  p50: number;
  p90: number;
  mean: number;
  /** Fraction of joint posterior draws where this policy beats the baseline. */
  probBetterThanBaseline: number | null;
  /** Observational data can never emit a causal winner (criterion 9). */
  claim: Extract<ClaimStrength, 'scenario'>;
  blockedReasons: string[];
}

export interface OfferValueSummary {
  offerId: string;
  model: 'geometric' | 'upfront' | 'trial_scaled' | 'weekly_geometric' | 'one_time';
  /** Present when the census forced the honest fallback (r2 <= r1). */
  fallbackReason?: 'r2_not_rising';
  p10: number;
  p50: number;
  p90: number;
  mean: number;
  claim: Extract<ClaimStrength, 'scenario'>;
}

export interface FrontierResult {
  modelVersion: string;
  seed: number;
  draws: number;
  horizonMonths: number;
  valueBasis: FrontierInput['valueBasis'];
  /** Deterministic hash of the normalized input bundle (replay identity). */
  inputHash: string;
  exposure: ExposureLanes & { coverageShare: number };
  offerValues: OfferValueSummary[];
  frontier: FrontierRow[];
  /** Criterion 9 — stamped on every result from observational evidence. */
  causalWinnerAllowed: false;
  notes: string[];
}
